import { useQuery } from "@tanstack/react-query";
import { Copy, KeyRound, RotateCcw, Send } from "lucide-react";
import {
  useDataProvider,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import { NativeSelect } from "../funnel/shared";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";

export const NORMALIZED = [
  "new",
  "contacted",
  "qualified",
  "meeting",
  "won",
] as const;
const CLOSED = ["lost", "junk", "no_answer"] as const;
const REASONS = [
  "expensive",
  "not_now",
  "no_answer",
  "not_target",
  "competitor",
  "other",
] as const;

type Settings = {
  enabled?: boolean;
  webhook_url?: string | null;
  stage_map?: Record<string, string>;
  reason_map?: Record<string, { status: string; reason: string }>;
};
type Key = {
  id: number;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};
type OutboxRow = {
  id: number;
  event: string;
  status: "pending" | "sending" | "sent" | "failed";
  attempts: number;
  last_status_code: number | null;
  last_error: string | null;
  created_at: string;
  next_attempt_at: string;
};
type Baseline = {
  captured_at: string;
  leads_per_month: number | null;
  conversion_pct: number | null;
  avg_check: number | null;
  revenue_per_month: number | null;
  source: string;
  note: string | null;
};

const rpc = async <T,>(name: string, args?: Record<string, unknown>) => {
  const { data, error } = await getSupabaseClient().rpc(name, args);
  if (error) throw error;
  return data as T;
};

const Section = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) => (
  <section className="space-y-3 border-t pt-5 first:border-0 first:pt-0">
    <div>
      <h3 className="font-semibold">{title}</h3>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
    {children}
  </section>
);

/** Settings → Connectors → Client Ops: keys, webhook, stage map, log, point zero */
export const ClientOpsConnector = () => {
  const translate = useTranslate();
  const t = (key: string, args?: Record<string, unknown>) =>
    translate(`crm.settings.connectors.client_ops.${key}`, args);
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [locale] = useLocaleState();
  const { dealStages, dealLostReasons } = useConfigurationContext();
  const { data: row, refetch: refetchSettings } = useGetOne<{
    id: string;
    value: Settings;
  }>("connector_settings", { id: "client_ops" });
  const settings = row?.value ?? {};
  const keys = useQuery({
    queryKey: ["client_ops_keys"],
    queryFn: () => rpc<Key[]>("client_ops_keys"),
  });
  const hasSecret = useQuery({
    queryKey: ["client_ops_has_secret"],
    queryFn: () => rpc<boolean>("client_ops_has_secret"),
  });
  const outbox = useQuery({
    queryKey: ["client_ops_outbox"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("client_ops_outbox")
        .select(
          "id, event, status, attempts, last_status_code, last_error, created_at, next_attempt_at",
        )
        .order("id", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as OutboxRow[];
    },
    refetchInterval: 15_000,
  });
  const baseline = useQuery({
    queryKey: ["client_ops_baseline"],
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("client_ops_baseline")
        .select("*")
        .maybeSingle();
      return data as Baseline | null;
    },
  });

  const [shownKey, setShownKey] = useState<string | null>(null);
  const [keyName, setKeyName] = useState("Client Ops");
  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [secret, setSecret] = useState("");
  const [shownSecret, setShownSecret] = useState<string | null>(null);
  const [stageMap, setStageMap] = useState<Record<string, string>>({});
  const [reasonMap, setReasonMap] = useState<Settings["reason_map"]>({});
  const [base, setBase] = useState<Partial<Baseline>>({});
  useEffect(() => {
    setUrl(settings.webhook_url ?? "");
    setEnabled(!!settings.enabled);
    setStageMap(settings.stage_map ?? {});
    setReasonMap(settings.reason_map ?? {});
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setBase(baseline.data ?? {}), [baseline.data]);

  const run = async (action: () => Promise<unknown>, ok?: string) => {
    try {
      await action();
      if (ok) notify(ok);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
  };
  const copy = (text: string) =>
    navigator.clipboard.writeText(text).then(
      () => notify(t("copied")),
      () => undefined,
    );
  const time = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString(locale, {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";
  const base_url = `${window.location.origin}/api/integrations/client-ops`;

  const saveWebhook = (newSecret: string | null) =>
    run(async () => {
      const result = await rpc<{ secret: string | null }>(
        "client_ops_set_webhook",
        { url, enabled, secret: newSecret },
      );
      if (result.secret) setShownSecret(result.secret);
      setSecret("");
      await refetchSettings();
      await hasSecret.refetch();
    }, t("saved"));

  const saveMaps = () =>
    run(async () => {
      await dataProvider.update("connector_settings", {
        id: "client_ops",
        data: {
          value: { ...settings, stage_map: stageMap, reason_map: reasonMap },
          updated_at: new Date().toISOString(),
        },
        previousData: row,
      });
      await refetchSettings();
    }, t("saved"));

  const stages = dealStages.filter(
    (s) => s.value !== "won" && s.value !== "lost",
  );
  const counts = {
    pending: outbox.data?.filter((r) => r.status === "pending").length ?? 0,
    failed: outbox.data?.filter((r) => r.status === "failed").length ?? 0,
  };

  return (
    <div className="space-y-5 rounded-xl border p-5">
      <p className="text-sm text-muted-foreground">{t("about")}</p>

      <Section title={t("endpoints")} hint={t("endpoints_hint")}>
        <div className="space-y-1 font-mono text-xs">
          {[
            "POST /leads",
            "GET /leads?updated_since=…&limit=200&cursor=…",
            "GET /stages",
            "GET /summary?from=…&to=…",
          ].map((line) => (
            <div key={line} className="rounded bg-muted px-2 py-1 break-all">
              {line.split(" ")[0]} {base_url}
              {line.split(" ")[1]}
            </div>
          ))}
        </div>
      </Section>

      <Section title={t("keys")} hint={t("keys_hint")}>
        {shownKey ? (
          <div className="space-y-2 rounded-lg border border-[#d89a2b] bg-[#fbefd2] p-3 text-sm text-[#3d2a00]">
            <b>{t("key_once")}</b>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-white/70 px-2 py-1 text-xs break-all">
                {shownKey}
              </code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => copy(shownKey)}
              >
                <Copy className="size-3.5" />
              </Button>
            </div>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            className="w-56"
          />
          <Button
            type="button"
            onClick={() =>
              run(async () => {
                setShownKey(
                  await rpc<string>("client_ops_create_key", { name: keyName }),
                );
                await keys.refetch();
                await baseline.refetch();
              })
            }
          >
            <KeyRound className="mr-1 size-4" />
            {t("create_key")}
          </Button>
        </div>
        {keys.data?.length ? (
          <ul className="divide-y rounded-lg border text-sm">
            {keys.data.map((key) => (
              <li
                key={key.id}
                className="flex flex-wrap items-center gap-3 px-3 py-2"
              >
                <span className="font-semibold">{key.name}</span>
                <code className="text-xs text-muted-foreground">
                  {key.prefix}…
                </code>
                <span className="text-xs text-muted-foreground">
                  {t("last_used", { at: time(key.last_used_at) })}
                </span>
                {key.revoked_at ? (
                  <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs">
                    {t("revoked")}
                  </span>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="ml-auto text-destructive"
                    onClick={() =>
                      window.confirm(t("revoke_confirm")) &&
                      run(async () => {
                        await rpc("client_ops_revoke_key", { key_id: key.id });
                        await keys.refetch();
                      })
                    }
                  >
                    {t("revoke")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </Section>

      <Section title={t("webhook")} hint={t("webhook_hint")}>
        <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
          <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
            {t("webhook_url")}
            <Input
              value={url}
              placeholder="https://…"
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            {t("enabled")}
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
          <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
            {t(hasSecret.data ? "secret_set" : "secret_none")}
            <Input
              type="password"
              value={secret}
              autoComplete="off"
              placeholder={t("secret_placeholder")}
              onChange={(e) => setSecret(e.target.value)}
            />
          </label>
          <Button
            type="button"
            variant="outline"
            onClick={() => saveWebhook("")}
          >
            {t("generate_secret")}
          </Button>
          <Button type="button" onClick={() => saveWebhook(secret || null)}>
            {t("save")}
          </Button>
        </div>
        {shownSecret ? (
          <div className="space-y-2 rounded-lg border border-[#d89a2b] bg-[#fbefd2] p-3 text-sm text-[#3d2a00]">
            <b>{t("secret_once")}</b>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-white/70 px-2 py-1 text-xs break-all">
                {shownSecret}
              </code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => copy(shownSecret)}
              >
                <Copy className="size-3.5" />
              </Button>
            </div>
          </div>
        ) : null}
      </Section>

      <Section title={t("map")} hint={t("map_hint")}>
        <div className="grid gap-2 sm:grid-cols-2">
          {stages.map((stage) => (
            <label
              key={stage.value}
              className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm"
            >
              <span className="font-semibold">{stage.label}</span>
              <NativeSelect
                value={stageMap[stage.value] ?? "new"}
                onChange={(e) =>
                  setStageMap({ ...stageMap, [stage.value]: e.target.value })
                }
              >
                {NORMALIZED.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </NativeSelect>
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{t("map_fixed")}</p>
        <h4 className="pt-2 text-sm font-semibold">{t("reasons")}</h4>
        <div className="space-y-2">
          {dealLostReasons.map((reason) => {
            const current = reasonMap?.[reason.value] ?? {
              status: "lost",
              reason: "other",
            };
            return (
              <div
                key={reason.value}
                className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm"
              >
                <span className="flex-1 font-semibold">{reason.label}</span>
                <NativeSelect
                  value={current.status}
                  onChange={(e) =>
                    setReasonMap({
                      ...reasonMap,
                      [reason.value]: { ...current, status: e.target.value },
                    })
                  }
                >
                  {CLOSED.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </NativeSelect>
                <NativeSelect
                  value={current.reason}
                  onChange={(e) =>
                    setReasonMap({
                      ...reasonMap,
                      [reason.value]: { ...current, reason: e.target.value },
                    })
                  }
                >
                  {REASONS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            );
          })}
        </div>
        <Button type="button" onClick={saveMaps}>
          {t("save")}
        </Button>
      </Section>

      <Section
        title={t("log")}
        hint={t("log_hint", { pending: counts.pending, failed: counts.failed })}
      >
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            run(async () => {
              await rpc("client_ops_test_event");
              await outbox.refetch();
            }, t("test_queued"))
          }
        >
          <Send className="mr-1 size-4" />
          {t("test_event")}
        </Button>
        {outbox.data?.length ? (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-3 py-2">{t("col_time")}</th>
                  <th className="px-3 py-2">{t("col_event")}</th>
                  <th className="px-3 py-2">{t("col_status")}</th>
                  <th className="px-3 py-2">{t("col_code")}</th>
                  <th className="px-3 py-2">{t("col_attempts")}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {outbox.data.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">
                      {time(r.created_at)}
                    </td>
                    <td className="px-3 py-1.5 font-mono">{r.event}</td>
                    <td className="px-3 py-1.5">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 font-semibold",
                          r.status === "sent" && "bg-[#e3f1e8] text-[#17703c]",
                          r.status === "failed" &&
                            "bg-destructive/10 text-destructive",
                          (r.status === "pending" || r.status === "sending") &&
                            "bg-[#fbefd2] text-[#7a4f00]",
                        )}
                        title={r.last_error ?? undefined}
                      >
                        {t(`status.${r.status}`)}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 tabular-nums">
                      {r.last_status_code ?? "—"}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums">{r.attempts}</td>
                    <td className="px-3 py-1.5 text-right">
                      {r.status === "failed" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            run(async () => {
                              await rpc("client_ops_retry", {
                                outbox_id: r.id,
                              });
                              await outbox.refetch();
                            })
                          }
                        >
                          <RotateCcw className="mr-1 size-3.5" />
                          {t("retry")}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("log_empty")}</p>
        )}
      </Section>

      <Section title={t("baseline")} hint={t("baseline_hint")}>
        <p className="text-xs text-muted-foreground">
          {baseline.data
            ? t("baseline_at", {
                at: time(baseline.data.captured_at),
                source: t(`baseline_source.${baseline.data.source}`),
              })
            : t("baseline_none")}
        </p>
        <div className="grid gap-3 sm:grid-cols-4">
          {(
            [
              "leads_per_month",
              "conversion_pct",
              "avg_check",
              "revenue_per_month",
            ] as const
          ).map((key) => (
            <label
              key={key}
              className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground"
            >
              {t(`b.${key}`)}
              <Input
                inputMode="decimal"
                value={base[key] ?? ""}
                onChange={(e) =>
                  setBase({
                    ...base,
                    [key]:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
            </label>
          ))}
        </div>
        <Input
          value={base.note ?? ""}
          placeholder={t("b.note")}
          onChange={(e) => setBase({ ...base, note: e.target.value })}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              run(async () => {
                await rpc("client_ops_capture_baseline");
                await baseline.refetch();
              }, t("saved"))
            }
          >
            {t("baseline_capture")}
          </Button>
          <Button
            type="button"
            onClick={() =>
              run(async () => {
                await rpc("client_ops_save_baseline", { values: base });
                await baseline.refetch();
              }, t("saved"))
            }
          >
            {t("baseline_save")}
          </Button>
        </div>
      </Section>
    </div>
  );
};
